import React, { useState, useEffect, useCallback } from 'react';
import { getSupabaseClient } from '../../lib/supabase';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  X, 
  Wallet, 
  Plus, 
  Printer, 
  Share2, 
  RefreshCw, 
  Loader2, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Calendar, 
  CheckCircle2, 
  Info,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

export interface LedgerEntry {
  date: string;
  kind: 'earning' | 'payout' | 'advance' | 'deduction';
  description: string;
  credit: number;
  debit: number;
  balance: number;
  ref_id?: string | null;
}

export interface ProfessionalLedgerData {
  professional_id?: string;
  professional_name: string;
  commission_percent: number;
  opening_balance: number;
  total_earned: number;
  total_paid: number;
  closing_balance: number;
  entries: LedgerEntry[];
}

interface ProfessionalLedgerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  professionalId: string;
  professionalName?: string;
  readOnly?: boolean;
  inline?: boolean;
}

export function ProfessionalLedgerDrawer({
  isOpen,
  onClose,
  professionalId,
  professionalName,
  readOnly = false,
  inline = false,
}: ProfessionalLedgerDrawerProps) {
  const supabase = getSupabaseClient();

  const [loading, setLoading] = useState(true);
  const [ledgerData, setLedgerData] = useState<ProfessionalLedgerData | null>(null);

  // Date filters
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  // Add Payment Modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payKind, setPayKind] = useState<'payout' | 'advance' | 'deduction'>('payout');
  const [payAmount, setPayAmount] = useState<string>('');
  const [payMethod, setPayMethod] = useState<'cash' | 'upi' | 'card' | 'bank' | 'online' | 'cheque'>('cash');
  const [payDate, setPayDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [payNote, setPayNote] = useState<string>('');
  const [submittingPayment, setSubmittingPayment] = useState(false);

  const fetchLedger = useCallback(async () => {
    if (!supabase || !professionalId) return;
    setLoading(true);

    try {
      const { data, error } = await supabase.rpc('get_professional_ledger', {
        p_professional_id: professionalId,
        p_from: fromDate || null,
        p_to: toDate || null,
      });

      if (error) {
        throw error;
      }

      setLedgerData(data as ProfessionalLedgerData);
    } catch (err: any) {
      console.error('get_professional_ledger error:', err);
      toast.error(err.message || 'Failed to load professional ledger');
    } finally {
      setLoading(false);
    }
  }, [supabase, professionalId, fromDate, toDate]);

  useEffect(() => {
    if (isOpen && professionalId) {
      fetchLedger();
    }
  }, [isOpen, professionalId, fetchLedger]);

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !professionalId) return;

    const amt = parseFloat(payAmount);
    if (isNaN(amt) || amt <= 0) {
      toast.error('Please enter a valid payment amount greater than 0');
      return;
    }

    setSubmittingPayment(true);
    try {
      const { error } = await supabase.rpc('record_professional_payment', {
        p_professional_id: professionalId,
        p_kind: payKind,
        p_amount: amt,
        p_payment_method: payKind === 'deduction' ? null : payMethod,
        p_note: payNote.trim() || null,
        p_payment_date: payDate || new Date().toISOString().split('T')[0],
      });

      if (error) {
        throw error;
      }

      toast.success(
        payKind === 'advance' 
          ? 'Advance recorded successfully' 
          : payKind === 'deduction' 
            ? 'Deduction recorded successfully' 
            : 'Payout recorded successfully'
      );
      setShowPaymentModal(false);
      setPayAmount('');
      setPayNote('');
      await fetchLedger();
    } catch (err: any) {
      console.error('record_professional_payment error:', err);
      // Real Postgres error surfaced in toast
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setSubmittingPayment(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShareWhatsApp = () => {
    if (!ledgerData) return;
    const name = ledgerData.professional_name || professionalName || 'Professional';
    const text = `*Ledger Statement - ${name}*\n` +
      `Commission: ${ledgerData.commission_percent}%\n` +
      `Opening: ₹${ledgerData.opening_balance}\n` +
      `Total Earned: ₹${ledgerData.total_earned}\n` +
      `Total Paid: ₹${ledgerData.total_paid}\n` +
      `*Closing Balance: ₹${ledgerData.closing_balance}* (Due to Professional)\n` +
      `Generated on ${new Date().toLocaleDateString('en-IN')}`;
    
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (!isOpen) return null;

  const content = (
    <div className="space-y-6">
      {/* Drawer / Card Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-200 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Wallet size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {ledgerData?.professional_name || professionalName || 'Professional'} — Ledger
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Commission Agreement: <span className="font-semibold text-primary">{ledgerData?.commission_percent ?? 0}%</span> of lead service jobs
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchLedger}
            disabled={loading}
            className="h-8 text-xs gap-1.5"
            title="Refresh Ledger"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            className="h-8 text-xs gap-1.5"
            title="Print Clean Statement"
          >
            <Printer size={13} />
            <span className="hidden sm:inline">Print</span>
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleShareWhatsApp}
            className="h-8 text-xs gap-1.5 text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 border-emerald-200 dark:border-emerald-800"
            title="Share on WhatsApp"
          >
            <Share2 size={13} />
            <span className="hidden sm:inline">WhatsApp</span>
          </Button>

          {!readOnly && (
            <Button
              size="sm"
              onClick={() => {
                setPayAmount('');
                setPayNote('');
                setPayKind('payout');
                setShowPaymentModal(true);
              }}
              className="h-8 text-xs gap-1.5 bg-primary hover:bg-primary/90 text-white font-semibold shadow-xs"
            >
              <Plus size={14} />
              <span>Add payment</span>
            </Button>
          )}

          {!inline && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
              title="Close Drawer"
            >
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="border border-slate-200 dark:border-slate-800 shadow-xs bg-white dark:bg-slate-900">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">Opening</span>
            <div className="text-base sm:text-lg font-bold font-mono text-slate-700 dark:text-slate-300 mt-1">
              ₹{Number(ledgerData?.opening_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
          </CardContent>
        </Card>

        <Card className="border border-emerald-200 dark:border-emerald-900/60 shadow-xs bg-emerald-50/50 dark:bg-emerald-950/20">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider block">Total Earned</span>
            <div className="text-base sm:text-lg font-bold font-mono text-emerald-600 dark:text-emerald-400 mt-1 flex items-center gap-1">
              <ArrowDownLeft size={16} />
              <span>₹{Number(ledgerData?.total_earned || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-blue-200 dark:border-blue-900/60 shadow-xs bg-blue-50/50 dark:bg-blue-950/20">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider block">Paid Out</span>
            <div className="text-base sm:text-lg font-bold font-mono text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
              <ArrowUpRight size={16} />
              <span>₹{Number(ledgerData?.total_paid || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-primary/30 shadow-xs bg-primary/5 dark:bg-primary/10">
          <CardContent className="p-3.5 sm:p-4">
            <span className="text-[11px] font-semibold text-primary uppercase tracking-wider block">Closing Balance</span>
            <div className="text-base sm:text-lg font-bold font-mono text-slate-900 dark:text-white mt-1">
              ₹{Number(ledgerData?.closing_balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <span className="text-[10px] text-slate-500 mt-0.5 block">
              {(Number(ledgerData?.closing_balance || 0) > 0) 
                ? 'Business owes professional' 
                : (Number(ledgerData?.closing_balance || 0) < 0) 
                  ? 'Professional advance excess' 
                  : 'Settled'}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-800 text-xs">
        <div className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300 font-medium">
          <Calendar size={14} className="text-slate-400" />
          <span>Period Filter:</span>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-slate-500 text-[11px]">From:</label>
          <Input
            type="date"
            value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            className="h-8 text-xs w-36 bg-white dark:bg-slate-800"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="text-slate-500 text-[11px]">To:</label>
          <Input
            type="date"
            value={toDate}
            onChange={e => setToDate(e.target.value)}
            className="h-8 text-xs w-36 bg-white dark:bg-slate-800"
          />
        </div>

        {(fromDate || toDate) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate('');
              setToDate('');
            }}
            className="h-8 text-xs text-slate-500 hover:text-slate-900"
          >
            Clear Dates
          </Button>
        )}
      </div>

      {/* Ledger Table */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900 shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-800 text-[11px] font-bold uppercase text-slate-500 tracking-wider">
              <tr>
                <th className="py-3 px-4">Date</th>
                <th className="py-3 px-4">Type</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4 text-right">Credit (Earned)</th>
                <th className="py-3 px-4 text-right">Debit (Paid)</th>
                <th className="py-3 px-4 text-right">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <Loader2 size={20} className="animate-spin mx-auto mb-2 text-primary" />
                    <span>Loading ledger transactions...</span>
                  </td>
                </tr>
              ) : !ledgerData?.entries || ledgerData.entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400">
                    <Info size={24} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                    <span>No ledger entries found for this period.</span>
                  </td>
                </tr>
              ) : (
                ledgerData.entries.map((entry, index) => {
                  const isEarning = entry.kind === 'earning';
                  const isPayout = entry.kind === 'payout';
                  const isAdvance = entry.kind === 'advance';
                  const isDeduction = entry.kind === 'deduction';

                  return (
                    <tr key={entry.ref_id || index} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 px-4 font-mono text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        {entry.date ? new Date(entry.date).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          isEarning 
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
                            : isPayout
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                              : isAdvance
                                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
                        }`}>
                          {entry.kind}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-800 dark:text-slate-200 font-medium">
                        {entry.description}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                        {Number(entry.credit) > 0 ? `+₹${Number(entry.credit).toFixed(2)}` : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-rose-600 dark:text-rose-400">
                        {Number(entry.debit) > 0 ? `-₹${Number(entry.debit).toFixed(2)}` : '—'}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-900 dark:text-white">
                        ₹{Number(entry.balance).toFixed(2)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Note under the table */}
      <div className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-800 text-[11px] text-slate-500 dark:text-slate-400 flex items-start gap-2">
        <Info size={14} className="text-primary mt-0.5 shrink-0" />
        <span>Earning = job value x (100 - commission%) for jobs completed by this professional as lead.</span>
      </div>

      {/* Add Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-60 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl animate-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 mb-4">
              <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <DollarSign size={18} className="text-primary" />
                <span>Record Professional Payment</span>
              </h3>
              <button
                type="button"
                onClick={() => setShowPaymentModal(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-600"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleRecordPayment} className="space-y-4 text-xs">
              {/* Payment Kind */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Payment Kind <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg font-semibold">
                  {(['payout', 'advance', 'deduction'] as const).map(k => (
                    <button
                      type="button"
                      key={k}
                      onClick={() => setPayKind(k)}
                      className={`py-1.5 text-center capitalize rounded-md transition-all ${
                        payKind === k
                          ? 'bg-white dark:bg-slate-700 text-primary shadow-xs font-bold'
                          : 'text-slate-600 dark:text-slate-400'
                      }`}
                    >
                      {k}
                    </button>
                  ))}
                </div>
                <span className="text-[10px] text-slate-400 mt-1 block">
                  {payKind === 'deduction'
                    ? 'Deduction adjusts ledger only (no cash/bank expense created).'
                    : 'Payout/advance posts an expense under "Salaries / Wages" automatically.'}
                </span>
              </div>

              {/* Amount */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Amount (₹) <span className="text-rose-500">*</span>
                </label>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="0.00"
                  value={payAmount}
                  onChange={e => setPayAmount(e.target.value)}
                  required
                  className="h-10 text-sm font-mono font-bold"
                  autoFocus
                />
              </div>

              {/* Payment Method (Hidden for Deduction) */}
              {payKind !== 'deduction' && (
                <div>
                  <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                    Payment Method
                  </label>
                  <select
                    value={payMethod}
                    onChange={e => setPayMethod(e.target.value as any)}
                    className="w-full h-10 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none focus:ring-1 focus:ring-primary capitalize"
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="online">Online</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
              )}

              {/* Payment Date */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Payment Date
                </label>
                <Input
                  type="date"
                  value={payDate}
                  onChange={e => setPayDate(e.target.value)}
                  required
                  className="h-10"
                />
              </div>

              {/* Note */}
              <div>
                <label className="font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                  Note / Reference
                </label>
                <Input
                  type="text"
                  placeholder="e.g. Festival advance, weekly settlement"
                  value={payNote}
                  onChange={e => setPayNote(e.target.value)}
                  className="h-10"
                />
              </div>

              {/* Modal Buttons */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPaymentModal(false)}
                  disabled={submittingPayment}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submittingPayment}
                  className="bg-primary hover:bg-primary/90 text-white font-semibold"
                >
                  {submittingPayment ? (
                    <div className="flex items-center gap-1.5">
                      <Loader2 size={14} className="animate-spin" />
                      <span>Recording...</span>
                    </div>
                  ) : (
                    'Record Payment'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="p-4 sm:p-6 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
        {content}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex justify-end">
      <div className="bg-white dark:bg-slate-900 w-full max-w-4xl h-full shadow-2xl p-6 overflow-y-auto animate-in slide-in-from-right duration-200">
        {content}
      </div>
    </div>
  );
}

export default ProfessionalLedgerDrawer;
